import { useEffect, useState, memo, useCallback, useRef } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import './Home.css';
import { Link } from 'react-router-dom';
import HeroCarousel from '../components/HeroCarousel';
import SradhaBlogs from '../components/SradhaBlogs';
import Partnerships from '../components/Partnerships';
import SEO from '../components/SEO/SEO';

const servicesData = [
  {
    id: 1,
    icon: 'fas fa-code',
    title: 'Next-Gen Development',
    description: 'Building tomorrow\'s digital experiences with cutting-edge tech stacks.',
    link: '/services/web-development',
  },
  {
    id: 2,
    icon: 'fas fa-mobile-alt',
    title: 'App Innovation Hub',
    description: 'Crafting mobile-first experiences that redefine user engagement.',
    link: '/services/mobile',
  },
  {
    id: 3,
    icon: 'fas fa-cloud',
    title: 'Cloud Transformation',
    description: 'Powering your digital evolution with scalable cloud solutions.',
    link: '/services/cloud',
  },
  {
    id: 4,
    icon: 'fas fa-shield-alt',
    title: 'Cyber Shield',
    description: 'Next-level security solutions for your digital ecosystem.',
    link: '/services/security',
  },
  {
    id: 5,
    icon: 'fas fa-database',
    title: 'Data Intelligence',
    description: 'Unlocking insights through smart data analytics and AI integration.',
    link: '/services/data',
  },
  {
    id: 6,
    icon: 'fas fa-rocket',
    title: 'Digital Acceleration',
    description: 'Fast-tracking your business growth with innovative tech strategies.',
    link: '/services/consulting',
  },
  {
    id: 7,
    icon: 'fas fa-chart-line',
    title: 'Observability & Monitoring',
    description: 'Real-time visibility and proactive issue detection across your digital infrastructure.',
    link: '/services/observability',
  },
];

const productsData = [
  {
    id: 'answerrank',
    icon: 'fas fa-brain',
    name: 'FirmaSearch',
    tagline: 'AI Visibility & AEO Platform',
    description:
      'Track your brand inside AI responses from ChatGPT, Claude, and Gemini. Close the visibility gap before your competitors do.',
    features: ['AI Response Tracking', 'Brand Citation Analysis', 'Competitor Gap Detection', 'AI-Optimized Content'],
    link: '/products/firmasearch',
    external: false,
  },
  {
    id: 'hiresmart',
    icon: 'fas fa-user-tie',
    name: 'HireSmart',
    tagline: 'Intelligent Talent Acquisition',
    description:
      'Match the right tech talent to the right roles — faster and with less friction. Automate shortlisting, scheduling, and analytics.',
    features: ['Smart Candidate Matching', 'Automated Shortlisting', 'Interview Scheduling', 'Hiring Analytics'],
    link: 'https://hiresmart.sradhatech.com',
    external: true,
  },
  {
    id: 'datanexis',
    icon: 'fas fa-project-diagram',
    name: 'Orchix Ai',
    tagline: 'Zero-Code Enterprise Data Pipelines',
    description:
      'Registry-driven data pipeline platform built on Microsoft Fabric, PySpark, and Delta Lake. Configure pipelines through metadata — no ETL code required.',
    features: ['Registry-Driven Pipelines', 'SCD1 / SCD2 / Append', 'Data Quality Engine', 'Full Audit Tracking'],
    link: '/products/orchix-ai',
    external: false,
  },
];

const projectsData = [
  {
    id: 1,
    title: 'E-Commerce Platform',
    description: 'Built a scalable e-commerce solution with mobile apps',
    image: '/images/projects/ecommerce.jpg',
    link: '/case-studies/ecommerce',
  },
  {
    id: 2,
    title: 'Healthcare Management',
    description: 'Comprehensive healthcare management system',
    image: '/images/projects/healthcare.jpg',
    link: '/case-studies/healthcare',
  },
  {
    id: 3,
    title: 'EdTech Platform',
    description: 'Online learning management system',
    image: '/images/projects/education.jpg',
    link: '/case-studies/edtech',
  },
];

const statsData = [
  {
    label: 'Projects Completed',
    value: '20+',
    icon: 'fas fa-project-diagram',
  },
  { label: 'Happy Clients', value: '10+', icon: 'fas fa-smile' },
  { label: 'Team Experts', value: '25+', icon: 'fas fa-users' },
  { label: 'Years Experience', value: '5+', icon: 'fas fa-calendar-check' },
];

const benefitsData = [
  {
    id: 1,
    icon: 'fas fa-rocket',
    title: 'Fast Delivery',
    description: 'Quick turnaround time with quality results',
  },
  {
    id: 2,
    icon: 'fas fa-shield-alt',
    title: 'Secure Solutions',
    description: 'Enterprise-grade security for all projects',
  },
  {
    id: 3,
    icon: 'fas fa-users',
    title: 'Expert Team',
    description: 'Skilled professionals with years of experience',
  },
  {
    id: 4,
    icon: 'fas fa-clock',
    title: '24/7 Support',
    description: 'Round-the-clock technical support and maintenance',
  },
  {
    id: 5,
    icon: 'fas fa-chart-line',
    title: 'Scalable Solutions',
    description: 'Solutions that grow with your business',
  },
  {
    id: 6,
    icon: 'fas fa-dollar-sign',
    title: 'Cost Effective',
    description: 'Competitive pricing with maximum value',
  },
];

const Home = () => {
  const initialized = useRef(false);
  const [scrollInfo, setScrollInfo] = useState({
    show: true,
    isTop: true,
    isBottom: false
  });
  const [lastScrollY, setLastScrollY] = useState(0);

  const handleScrollClick = useCallback((direction) => {
    window.scrollTo({
      top: direction === 'up' ? 0 : document.documentElement.scrollHeight,
      behavior: 'smooth'
    });
  }, []);

  const handleScroll = useCallback(() => {
    const scrolled = window.scrollY;
    const viewportHeight = window.innerHeight;
    const totalHeight = document.documentElement.scrollHeight;

    const isTop = scrolled < 100;
    const isBottom = scrolled > (totalHeight - viewportHeight - 20);

    setScrollInfo(prev => ({
      show: !isBottom,
      isTop,
      isBottom,
      scrollingUp: scrolled < lastScrollY
    }));
    setLastScrollY(scrolled);
  }, [lastScrollY]);

  useEffect(() => {
    if (!initialized.current) {
      const isMobile = window.innerWidth < 768;

      AOS.init({
        duration: 600,
        once: true,
        offset: isMobile ? 20 : 100,
        delay: 0,
        easing: 'ease-out',
        mirror: false,
        anchorPlacement: 'top-bottom',
        disable: () => window.innerWidth < 768 && 'phone',
      });
      initialized.current = true;
    } else {
      AOS.refresh();
    }

    const cleanup = () => {
      window.removeEventListener('resize', AOS.refresh);
      window.removeEventListener('scroll', handleScroll);
    };

    window.addEventListener('resize', AOS.refresh);
    window.addEventListener('scroll', handleScroll);

    return cleanup;
  }, [handleScroll]);

  const renderServices = useCallback(() => (
    <section
      className="services-section"
      data-aos="fade-up"
      data-aos-duration="600"
      data-aos-offset="20"
      data-aos-delay="0"
    >
      <div className="container">
        <h2
          className="section-title"
          data-aos="fade-up"
          data-aos-duration="400"
        >
          Our Services
        </h2>
        <div className="features-grid">
          {servicesData.map((service, index) => (
            <div
              key={service.id}
              className="feature-card"
              data-aos="fade-up"
              data-aos-duration="400"
              data-aos-delay={
                window.innerWidth < 768 ? '0' : `${index * 50}`
              }
            >
              <div className="feature-icon">
                <i className={service.icon}></i>
              </div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <Link to={service.link} className="learn-more">
                Learn More
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  ), []);

  const renderProducts = useCallback(() => (
    <section
      className="products-section"
      data-aos="fade-up"
      data-aos-duration="600"
      data-aos-offset="20"
    >
      <div className="container">
        <h2 className="section-title" data-aos="fade-up" data-aos-duration="400">
          Our Products
        </h2>
        <div className="features-grid">
          {productsData.map((product, index) => (
            <div
              key={product.id}
              className="feature-card"
              data-aos="fade-up"
              data-aos-duration="400"
              data-aos-delay={window.innerWidth < 768 ? '0' : `${index * 50}`}
            >
              <div className="feature-icon">
                <i className={product.icon}></i>
              </div>
              <h3>{product.name}</h3>
              <p>{product.description}</p>
              {product.external ? (
                <a
                  href={product.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="learn-more"
                >
                  Learn More
                </a>
              ) : (
                <Link to={product.link} className="learn-more">
                  Learn More
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  ), []);

  const renderBenefits = useCallback(() => (
    <section
      className="why-choose-us"
      data-aos="fade-up"
      data-aos-duration="600"
      data-aos-offset="20"
    >
      <div className="container">
        <h2 className="section-title" data-aos="fade-up">
          Why Choose Us
        </h2>
        <div className="benefits-grid">
          {benefitsData.map((benefit, index) => (
            <div
              className="benefit-card"
              key={benefit.id}
              data-aos="zoom-in"
              data-aos-delay={index * 100}
            >
              <div className="benefit-icon">
                <i className={benefit.icon}></i>
              </div>
              <h3>{benefit.title}</h3>
              <p>{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  ), []);

  return (
    <>
      <SEO
        title="Leading Software Development Company"
        description="Sradha Technologies delivers cutting-edge software solutions, mobile app development, cloud services, and digital transformation for businesses worldwide."
        keywords="software development, mobile apps, cloud computing, digital transformation, IT services, technology solutions"
        canonical="https://sradhatech.com"
      />
      <div className="home-page">
        <section className="hero-section">
          <HeroCarousel />
        </section>

        {renderServices()}

        {renderProducts()}

        {renderBenefits()}

        <section
          className="partnerships-section"
          data-aos="fade-up"
          data-aos-duration="600"
          data-aos-offset="20"
        >
          <Partnerships />
        </section>

        <SradhaBlogs />

        <section className="stats-section" data-aos="fade-up">
          <div className="container">
            <div className="stats-grid">
              {statsData.map((stat, index) => (
                <div
                  key={index}
                  className="stat-item"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="stat-icon">
                    <i className={stat.icon}></i>
                  </div>
                  <h3>{stat.value}</h3>
                  <p>{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="cta-section" data-aos="fade-up">
          <div className="container">
            <h2>Ready to Transform Your Business?</h2>
            <p>
              Let&apos;s discuss how Sradha Technologies can help you achieve
              your goals
            </p>
            <Link to="/contact" className="btn btn-primary">
              Schedule a Consultation
            </Link>
          </div>
        </section>
        <div className={`scroll-indicator-wrapper ${!scrollInfo.show ? 'hide' : ''}`}>
          <div className="scroll-indicator">
            <div
              className={`scroll-arrow up ${scrollInfo.scrollingUp ? 'active' : ''}`}
              onClick={() => handleScrollClick('up')}
              role="button"
              tabIndex={0}
              aria-label="Scroll to top"
            >
              <i className="fas fa-chevron-up"></i>
            </div>
            <div className="scroll-line"></div>
            <div
              className={`scroll-arrow down ${!scrollInfo.scrollingUp ? 'active' : ''}`}
              onClick={() => handleScrollClick('down')}
              role="button"
              tabIndex={0}
              aria-label="Scroll to bottom"
            >
              <i className="fas fa-chevron-down"></i>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

Home.displayName = 'Home';

const MemoizedHome = memo(Home, (prevProps, nextProps) => true);
MemoizedHome.displayName = 'MemoizedHome';
export default MemoizedHome;
